import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { parseBackendDateTime } from '@signals/app-shell/utils/parseDateTime';
import { Group } from '@visx/group';
import { scaleBand, scaleTime, scaleLinear } from '@visx/scale';
import { AxisBottom } from '@visx/axis';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { Brush } from '@visx/brush';
import BaseBrush from '@visx/brush/lib/BaseBrush';
import { PatternLines } from '@visx/pattern';
import { Bounds } from '@visx/brush/lib/types';
import { BrushHandleRenderProps } from '@visx/brush/lib/BrushHandle';
import { 
  Box, 
  Typography, 
  Chip, 
  Stack, 
  IconButton, 
  Tooltip as MuiTooltip,
  useTheme,
  alpha,
  Select,
  MenuItem,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PhoneIcon from '@mui/icons-material/Phone';
import SmsIcon from '@mui/icons-material/Sms';
import PlaceIcon from '@mui/icons-material/Place';
import LanguageIcon from '@mui/icons-material/Language';
import AppsIcon from '@mui/icons-material/Apps';

// Types
interface Node {
  id: string;
  type?: string;
  label?: string;
  entity_type?: string;
  place_name?: string;
}

interface Link {
  source: string | Node;
  target: string | Node;
  type?: string;
  timestamp?: string;
  weight?: number;
}

interface LinkAnalysisTimelineHeatmapProps {
  nodes: Node[];
  links: Link[];
  // Original unfiltered data for the mini timeline (always shows full extent)
  originalNodes?: Node[];
  originalLinks?: Link[];
  onDateFilter?: (dates: Set<string> | null) => void;
  onEntityFilter?: (entityIds: string[]) => void;
  onConnectionTypeFilter?: (types: Set<string> | null) => void;
  selectedDates?: Set<string> | null;
  selectedConnectionTypes?: Set<string> | null;
  height?: number;
}

interface TimelineEvent {
  entityId: string;
  entityLabel: string;
  entityType: string;
  timestamp: Date;
  type: string;
  targetId: string;
  targetLabel: string;
}

interface EntityCluster {
  id: string;
  label: string;
  type: string;
  events: TimelineEvent[];
  color: string;
  tier: 1 | 2 | 3; // 1=Hub, 2=Secondary, 3=Other
  connectionCount: number;
}

interface TooltipData {
  entity: string;
  date: string;
  counts: { [key: string]: number };
  total: number;
}

// Color palette for connection types
const CONNECTION_COLORS: { [key: string]: string } = {
  call: '#3b82f6',      // Blue
  sms: '#22c55e',       // Green  
  dwell: '#f59e0b',     // Amber
  browsing: '#8b5cf6',  // Purple
  app: '#ec4899',       // Pink
};

// Color palette for clusters (entities)
const CLUSTER_COLORS = [
  '#00d4aa', // Teal
  '#ffd93d', // Yellow
  '#6bcb77', // Green
  '#4d96ff', // Blue
  '#ff6b6b', // Red/Coral
  '#c44cff', // Purple
  '#ff8c42', // Orange
  '#48dbfb', // Cyan
  '#ff9ff3', // Pink
  '#1abc9c', // Emerald
];

// Tooltip styles
const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: 'rgba(15, 23, 42, 0.95)',
  color: '#f8fafc',
  padding: '12px 16px',
  fontSize: '12px',
  borderRadius: '8px',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(8px)',
};

const LinkAnalysisTimelineHeatmap: React.FC<LinkAnalysisTimelineHeatmapProps> = ({
  nodes,
  links,
  originalNodes,
  originalLinks,
  onDateFilter,
  onEntityFilter,
  onConnectionTypeFilter,
  selectedDates,
  selectedConnectionTypes,
  height = 500,
}) => {
  // Use original data for mini timeline (always shows full extent)
  // Use filtered data (nodes/links) for main heatmap
  const miniTimelineNodes = originalNodes || nodes;
  const miniTimelineLinks = originalLinks || links;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const containerRef = useRef<HTMLDivElement>(null);
  const brushRef = useRef<BaseBrush | null>(null);
  const [containerWidth, setContainerWidth] = useState(1000);
  const isMounted = useRef(false); // Prevent filter on initial mount
  const brushStartTime = useRef<number>(0); // Track when brush interaction started
  const lastBrushDomain = useRef<string>(''); // Track the last brush domain to prevent duplicate triggers
  const userClickedBrush = useRef(false); // Track if user actually clicked in the brush area
  
  // State
  const [_panOffset, setPanOffset] = useState(0);
  const allTypes = new Set(['call', 'sms', 'dwell', 'browsing', 'app']);
  const [localSelectedTypes, setLocalSelectedTypes] = useState<Set<string>>(allTypes);
  const [showDensity, setShowDensity] = useState(true);
  const [showOverlapOnly, setShowOverlapOnly] = useState(false); // Filter to show only overlapping activity
  const [overlapMinutes, setOverlapMinutes] = useState(10); // Configurable overlap time window
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [brushExtent, setBrushExtent] = useState<[Date, Date] | null>(null);
  const [brushResetKey, setBrushResetKey] = useState(0); // Key to force brush remount on reset
  
  // Use external connection types if provided and not empty, otherwise use local state
  // null = no filter (show all), empty Set = filter active but nothing selected
  const selectedTypes = selectedConnectionTypes && selectedConnectionTypes.size > 0 
    ? selectedConnectionTypes 
    : localSelectedTypes;
  
  // Sync local state when external filter changes
  useEffect(() => {
    if (selectedConnectionTypes && selectedConnectionTypes.size > 0) {
      setLocalSelectedTypes(new Set(selectedConnectionTypes));
    }
  }, [selectedConnectionTypes]);
  
  // Reset brush when external date filter is cleared
  useEffect(() => {
    // If selectedDates is null (filter cleared) and we have a brush extent, reset it
    if (selectedDates === null) {
      console.log('[Timeline] External date filter cleared, resetting brush. Current brushExtent:', brushExtent);
      // Always reset brush state when filter is cleared
      setBrushExtent(null);
      brushStartTime.current = 0;
      lastBrushDomain.current = '';
      userClickedBrush.current = false;
      // Force brush to remount by changing its key
      setBrushResetKey(prev => prev + 1);
      console.log('[Timeline] Incremented brushResetKey to force brush reset');
    }
  }, [selectedDates]); // Only depend on selectedDates, not brushExtent
  
  // Tooltip
  const { containerRef: tooltipContainerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<TooltipData>();
  
  // Mark component as mounted after a longer delay to prevent initial brush triggers
  // The brush can fire spuriously during resize/layout events
  useEffect(() => {
    isMounted.current = false;
    brushStartTime.current = 0;
    userClickedBrush.current = false;
    lastBrushDomain.current = '';
    
    const timer = setTimeout(() => {
      isMounted.current = true;
    }, 2500); // Even longer delay to ensure all layout is settled
    
    return () => {
      clearTimeout(timer);
      isMounted.current = false;
      userClickedBrush.current = false;
    };
  }, []);
  
  // Resize observer
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  
  // Process data into timeline events
  const { events, clusters, dateRange } = useMemo(() => {
    const entityMap = new Map<string, Node>();
    nodes.forEach(node => entityMap.set(node.id, node));
    
    // Get device nodes only for clustering
    const deviceNodes = nodes.filter(n => n.type === 'device');
    
    // Process links into events
    const events: TimelineEvent[] = [];
    let minDate: Date | null = null;
    let maxDate: Date | null = null;
    
    links.forEach(link => {
      if (!link.timestamp) return;
      
      const timestamp = parseBackendDateTime(link.timestamp);
      if (!timestamp) return;
      
      // For min/max tracking, use LOCAL date at midnight to avoid timezone shifts
      const localDate = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate());
      if (!minDate || localDate < minDate) minDate = localDate;
      // For max date, use end of day to include all events on that day
      const localDateEnd = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate(), 23, 59, 59);
      if (!maxDate || localDateEnd > maxDate) maxDate = localDateEnd;
      
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      
      if (!sourceId || !targetId) return;
      
      const sourceNode = entityMap.get(sourceId);
      const targetNode = entityMap.get(targetId);
      
      // Only include events where source is a device
      if (sourceNode?.type !== 'device') return;
      
      events.push({
        entityId: sourceId,
        entityLabel: sourceNode?.label || sourceId,
        entityType: sourceNode?.type || 'unknown',
        timestamp,
        type: link.type || 'unknown',
        targetId,
        targetLabel: targetNode?.label || targetNode?.place_name || targetId,
      });
    });
    
    // Group events by entity
    const entityEvents = new Map<string, TimelineEvent[]>();
    events.forEach(event => {
      const existing = entityEvents.get(event.entityId) || [];
      existing.push(event);
      entityEvents.set(event.entityId, existing);
    });
    
    // Count connections per device (unique targets)
    const connectionCounts = new Map<string, Set<string>>();
    links.forEach(link => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source?.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target?.id;
      if (!sourceId || !targetId) return;
      
      const sourceNode = entityMap.get(sourceId);
      if (sourceNode?.type === 'device') {
        if (!connectionCounts.has(sourceId)) connectionCounts.set(sourceId, new Set());
        connectionCounts.get(sourceId)!.add(targetId);
      }
    });
    
    // Create clusters from ALL device nodes (with or without events)
    const clusters: EntityCluster[] = [];
    let colorIndex = 0;
    
    // Calculate max values for tier thresholds
    const maxEvents = Math.max(...deviceNodes.map(n => (entityEvents.get(n.id) || []).length), 1);
    const maxConnections = Math.max(...deviceNodes.map(n => connectionCounts.get(n.id)?.size || 0), 1);
    
    deviceNodes.forEach(node => {
      const nodeEvents = entityEvents.get(node.id) || [];
      const nodeConnections = connectionCounts.get(node.id)?.size || 0;
      
      // Calculate importance score for tier assignment
      const eventScore = nodeEvents.length / maxEvents;
      const connectionScore = nodeConnections / maxConnections;
      const importance = (eventScore * 0.6) + (connectionScore * 0.4);
      
      // Assign tier based on importance
      // Tier 1 (Hub): Top performers with high activity and connections
      // Tier 2 (Secondary): Moderate activity
      // Tier 3 (Other): Low or no activity
      let tier: 1 | 2 | 3;
      if (importance >= 0.3 || nodeEvents.length >= 10 || nodeConnections >= 5) {
        tier = 1; // Hub
      } else if (importance >= 0.1 || nodeEvents.length >= 3 || nodeConnections >= 2) {
        tier = 2; // Secondary
      } else {
        tier = 3; // Other
      }
      
      clusters.push({
        id: node.id,
        label: node.label || node.id,
        type: node.type || 'device',
        events: nodeEvents.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()),
        color: CLUSTER_COLORS[colorIndex % CLUSTER_COLORS.length],
        tier,
        connectionCount: nodeConnections,
      });
      colorIndex++;
    });
    
    // Sort clusters by tier first (1=Hub at top), then by event count
    clusters.sort((a, b) => {
      if (a.tier !== b.tier) return a.tier - b.tier;
      return b.events.length - a.events.length;
    });
    
    return {
      events,
      clusters,
      dateRange: minDate && maxDate ? [minDate, maxDate] as [Date, Date] : null,
      entityMap,
    };
  }, [nodes, links]);
  
  // Ref to lock in the original date range - never let it shrink during brush interactions
  const lockedDateRangeRef = useRef<[Date, Date] | null>(null);
  const lockedMiniHeatmapRef = useRef<{ date: Date; counts: { [key: string]: number }; total: number }[]>([]);
  const lastOriginalLinksCountRef = useRef<number>(0); // Track original data size to detect new fetches
  
  // Process ORIGINAL data for the mini timeline (always shows full extent)
  const { originalDateRange, miniHeatmapData } = useMemo(() => {
    // Use original data if available, otherwise use filtered data
    const dataNodes = miniTimelineNodes;
    const dataLinks = miniTimelineLinks;
    
    const entityMap = new Map<string, Node>();
    dataNodes.forEach(node => entityMap.set(node.id, node));
    
    // Get date range from original data
    let minDate: Date | null = null;
    let maxDate: Date | null = null;
    
    // Process links to get date range and mini heatmap data
    const dailyCounts = new Map<string, { date: Date; counts: { [key: string]: number }; total: number }>();
    
    dataLinks.forEach(link => {
      if (!link.timestamp) return;
      
      const timestamp = parseBackendDateTime(link.timestamp);
      if (!timestamp) return;
      
      // Track min/max date
      const localDate = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate());
      if (!minDate || localDate < minDate) minDate = localDate;
      const localDateEnd = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate(), 23, 59, 59);
      if (!maxDate || localDateEnd > maxDate) maxDate = localDateEnd;
      
      // Aggregate by day for mini heatmap
      const dateKey = localDate.toISOString().split('T')[0];
      if (!dailyCounts.has(dateKey)) {
        dailyCounts.set(dateKey, { date: localDate, counts: {}, total: 0 });
      }
      const entry = dailyCounts.get(dateKey)!;
      const type = link.type || 'unknown';
      entry.counts[type] = (entry.counts[type] || 0) + 1;
      entry.total += 1;
    });
    
    const computedRange = minDate && maxDate ? [minDate, maxDate] as [Date, Date] : null;
    const computedHeatmap = Array.from(dailyCounts.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
    
    // Detect if this is NEW data (user fetched different date range) vs filtered data
    // New data will typically have more links than filtered data
    const currentLinksCount = dataLinks.length;
    const isNewDataFetch = currentLinksCount > lastOriginalLinksCountRef.current * 1.1 || // 10% more links = new fetch
                           lastOriginalLinksCountRef.current === 0; // First load
    
    if (computedRange) {
      if (!lockedDateRangeRef.current || isNewDataFetch) {
        // First time OR new data fetch - lock it in
        lockedDateRangeRef.current = computedRange;
        lockedMiniHeatmapRef.current = computedHeatmap;
        lastOriginalLinksCountRef.current = currentLinksCount;
        console.log('[Timeline] Locked original date range:', computedRange[0].toLocaleDateString(), '-', computedRange[1].toLocaleDateString(), '(links:', currentLinksCount, ')');
      } else {
        // Only expand, never shrink (prevents brush filtering from shrinking mini timeline)
        const lockedDuration = lockedDateRangeRef.current[1].getTime() - lockedDateRangeRef.current[0].getTime();
        const newDuration = computedRange[1].getTime() - computedRange[0].getTime();
        
        if (newDuration > lockedDuration) {
          // New data has wider range - update locked range
          lockedDateRangeRef.current = computedRange;
          lockedMiniHeatmapRef.current = computedHeatmap;
          lastOriginalLinksCountRef.current = currentLinksCount;
          console.log('[Timeline] Expanded locked date range to:', computedRange[0].toLocaleDateString(), '-', computedRange[1].toLocaleDateString());
        }
      }
    }
    
    // Always return the locked (widest) range
    return {
      originalDateRange: lockedDateRangeRef.current,
      miniHeatmapData: lockedMiniHeatmapRef.current,
    };
  }, [miniTimelineNodes, miniTimelineLinks]);
  
  // Filtered clusters based on selected types - show ALL nodes, filter events only
  // Calculate center-based overlap - entities active within X min of brush center
  // This is simpler and more intuitive: "show me who was active at this moment"
  const { entitiesAtCenter, centerTime } = useMemo(() => {
    if (!brushExtent) {
      return { entitiesAtCenter: new Set<string>(), centerTime: null };
    }
    
    const overlapWindowMs = overlapMinutes * 60 * 1000;
    const center = new Date((brushExtent[0].getTime() + brushExtent[1].getTime()) / 2);
    const centerMs = center.getTime();
    
    // Find entities with events within the overlap window of the center
    const activeEntities = new Set<string>();
    
    clusters.forEach(cluster => {
      const hasActivityNearCenter = cluster.events
        .filter(e => selectedTypes.has(e.type))
        .some(event => {
          const timeDiff = Math.abs(event.timestamp.getTime() - centerMs);
          return timeDiff <= overlapWindowMs;
        });
      
      if (hasActivityNearCenter) {
        activeEntities.add(cluster.id);
      }
    });
    
    return {
      entitiesAtCenter: activeEntities,
      centerTime: center,
    };
  }, [clusters, selectedTypes, brushExtent, overlapMinutes]);
  
  // Filtered clusters based on selected types and overlap filter
  const filteredClusters = useMemo(() => {
    let result = clusters.map(cluster => ({
      ...cluster,
      events: cluster.events.filter(e => selectedTypes.has(e.type)),
    }));
    
    // If showing overlap only AND we have a brush selection, 
    // filter to entities active within 5 min of center line
    if (showOverlapOnly && brushExtent && entitiesAtCenter.size > 0) {
      result = result.filter(cluster => entitiesAtCenter.has(cluster.id));
    }
    
    return result;
  }, [clusters, selectedTypes, showOverlapOnly, brushExtent, entitiesAtCenter]);
  
  // Tier colors for indicators
  const TIER_COLORS: Record<number, string> = {
    1: '#ef4444', // Red for Hub
    2: '#3b82f6', // Blue for Secondary  
    3: '#6b7280', // Gray for Other
  };
  
  const TIER_LABELS: Record<number, string> = {
    1: 'HUB',
    2: 'SEC',
    3: 'OTH',
  };
  
  // Calculate dimensions - increased left margin to fit tier badges + labels
  const MARGIN = { top: 35, right: 20, bottom: 70, left: 140 };
  const MINI_HEIGHT = 80;
  const innerWidth = containerWidth - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom - MINI_HEIGHT - 40;
  const rowHeight = Math.max(20, Math.min(40, innerHeight / Math.max(filteredClusters.length, 1)));
  const actualHeight = Math.max(innerHeight, filteredClusters.length * rowHeight);
  
  // Time scale
  const timeScale = useMemo(() => {
    if (!dateRange) return null;
    
    const effectiveRange = brushExtent || dateRange;
    
    return scaleTime({
      domain: effectiveRange,
      range: [0, innerWidth],
    });
  }, [dateRange, innerWidth, brushExtent]);
  
  // Full time scale for mini timeline (uses original data for full extent)
  const fullTimeScale = useMemo(() => {
    // Use original date range if available, otherwise fall back to filtered date range
    const rangeToUse = originalDateRange || dateRange;
    if (!rangeToUse) return null;
    return scaleTime({
      domain: rangeToUse,
      range: [0, innerWidth],
    });
  }, [originalDateRange, dateRange, innerWidth]);
  
  // Entity scale
  const entityScale = useMemo(() => {
    return scaleBand({
      domain: filteredClusters.map(c => c.id),
      range: [0, actualHeight],
      padding: 0.2,
    });
  }, [filteredClusters, actualHeight]);
  
  // Create heatmap data (aggregated by day per entity)
  const heatmapData = useMemo(() => {
    if (!timeScale || !dateRange) return [];
    
    const data: {
      entityId: string;
      date: Date;
      counts: { [key: string]: number };
      total: number;
    }[] = [];
    
    // Get day boundaries
    const effectiveRange = brushExtent || dateRange;
    const startDay = new Date(effectiveRange[0]);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(effectiveRange[1]);
    endDay.setHours(23, 59, 59, 999);
    
    // Determine granularity based on date range
    const daysDiff = Math.ceil((endDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24));
    const granularity = daysDiff > 90 ? 'week' : daysDiff > 30 ? 'day' : 'hour';
    
    filteredClusters.forEach(cluster => {
      // Group events by time bucket
      const buckets = new Map<string, { date: Date; counts: { [key: string]: number } }>();
      
      cluster.events.forEach(event => {
        if (event.timestamp < effectiveRange[0] || event.timestamp > effectiveRange[1]) return;
        
        let bucketKey: string;
        let bucketDate: Date;
        
        if (granularity === 'week') {
          const d = new Date(event.timestamp);
          d.setHours(0, 0, 0, 0);
          d.setDate(d.getDate() - d.getDay());
          bucketKey = d.toISOString().split('T')[0];
          bucketDate = d;
        } else if (granularity === 'day') {
          const d = new Date(event.timestamp);
          d.setHours(0, 0, 0, 0);
          bucketKey = d.toISOString().split('T')[0];
          bucketDate = d;
        } else {
          const d = new Date(event.timestamp);
          d.setMinutes(0, 0, 0);
          bucketKey = d.toISOString();
          bucketDate = d;
        }
        
        if (!buckets.has(bucketKey)) {
          buckets.set(bucketKey, { date: bucketDate, counts: {} });
        }
        
        const bucket = buckets.get(bucketKey)!;
        bucket.counts[event.type] = (bucket.counts[event.type] || 0) + 1;
      });
      
      buckets.forEach((bucket, _key) => {
        const total = Object.values(bucket.counts).reduce((a, b) => a + b, 0);
        data.push({
          entityId: cluster.id,
          date: bucket.date,
          counts: bucket.counts,
          total,
        });
      });
    });
    
    return data;
  }, [filteredClusters, timeScale, dateRange, brushExtent]);
  
  // Find max count for color scaling
  const maxCount = useMemo(() => {
    return Math.max(...heatmapData.map(d => d.total), 1);
  }, [heatmapData]);
  
  // Intensity scale
  const intensityScale = scaleLinear({
    domain: [0, maxCount],
    range: [0.2, 1],
  });
  
  // Handle type toggle - updates both timeline and main graph
  const handleTypeToggle = useCallback((type: string) => {
    const newTypes = new Set(selectedTypes);
    if (newTypes.has(type)) {
      newTypes.delete(type);
    } else {
      newTypes.add(type);
    }
    if (newTypes.size > 0) {
      // Update local state
      setLocalSelectedTypes(newTypes);
      // Also update the main graph filter
      if (onConnectionTypeFilter) {
        onConnectionTypeFilter(newTypes);
      }
    }
  }, [selectedTypes, onConnectionTypeFilter]);
  
  // Helper to validate and create Date from brush bounds
  // The Brush component returns x values in the scale's domain units (timestamps for time scales)
  const getValidDate = useCallback((x: number): Date | null => {
    try {
      // x is already a timestamp in milliseconds
      const date = new Date(x);
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date;
      }
    } catch (e) {
      console.warn('Failed to create date from timestamp:', e);
    }
    return null;
  }, []);
  
  // Format date range for display
  const formatBrushRange = useMemo(() => {
    if (!brushExtent) return null;
    const [start, end] = brushExtent;
    
    // Validate dates
    if (!(start instanceof Date) || !(end instanceof Date)) return null;
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    
    return startStr === endStr ? startStr : `${startStr} - ${endStr}`;
  }, [brushExtent]);
  
  // Minimum brush width: 1 day in ms (ensures handles are grabbable)
  const MIN_BRUSH_WIDTH_MS = 24 * 60 * 60 * 1000;
  
  // Handle brush change (during drag) - only visual update, no filtering here
  const onBrushChange = useCallback((domain: Bounds | null) => {
    // STRICT CHECK: Only process if user actually clicked AND component is mounted
    if (!userClickedBrush.current) return;
    if (!isMounted.current) return;
    if (!domain) return;
    
    // Record when brush interaction started (only if not already set)
    if (brushStartTime.current === 0) {
      brushStartTime.current = Date.now();
    }
    
    let { x0, x1 } = domain;
    
    // Enforce minimum width of 1 day
    const width = Math.abs(x1 - x0);
    if (width < MIN_BRUSH_WIDTH_MS) {
      // Expand from center to meet minimum
      const center = (x0 + x1) / 2;
      x0 = center - MIN_BRUSH_WIDTH_MS / 2;
      x1 = center + MIN_BRUSH_WIDTH_MS / 2;
    }
    
    const newStart = getValidDate(x0);
    const newEnd = getValidDate(x1);
    if (newStart && newEnd) {
      setBrushExtent([newStart, newEnd]);
    }
  }, [getValidDate]);
  
  // Helper to get LOCAL date key (YYYY-MM-DD) - matches the format used by LinkAnalysisPage
  const getLocalDateKey = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  
  // Handle brush end (when user releases) - this triggers the actual filtering
  const onBrushEnd = useCallback((domain: Bounds | null) => {
    // STRICT CHECK 1: Must have user click first
    if (!userClickedBrush.current) {
      return;
    }
    
    // STRICT CHECK 2: Component must be fully mounted
    if (!isMounted.current) {
      brushStartTime.current = 0;
      userClickedBrush.current = false;
      return;
    }
    
    // STRICT CHECK 3: Interaction must have lasted more than 200ms (real user drag)
    const interactionDuration = Date.now() - brushStartTime.current;
    if (brushStartTime.current === 0 || interactionDuration < 200) {
      brushStartTime.current = 0;
      userClickedBrush.current = false;
      return;
    }
    
    // Reset tracking flags
    brushStartTime.current = 0;
    userClickedBrush.current = false;
    
    if (!domain) return;
    
    let { x0, x1 } = domain;
    
    // Enforce minimum width of 1 day
    const width = Math.abs(x1 - x0);
    if (width < MIN_BRUSH_WIDTH_MS) {
      const center = (x0 + x1) / 2;
      x0 = center - MIN_BRUSH_WIDTH_MS / 2;
      x1 = center + MIN_BRUSH_WIDTH_MS / 2;
    }
    
    // STRICT CHECK 4: Skip duplicate domains
    const domainKey = `${Math.round(x0)}-${Math.round(x1)}`;
    if (domainKey === lastBrushDomain.current) {
      return;
    }
    lastBrushDomain.current = domainKey;
    
    const newStart = getValidDate(x0);
    const newEnd = getValidDate(x1);
    if (newStart && newEnd) {
      setBrushExtent([newStart, newEnd]);
      
      // Apply date filter to the main graph
      if (onDateFilter) {
        // Generate all dates in the range using LOCAL date keys
        const dates = new Set<string>();
        const currentDate = new Date(newStart);
        const endDate = new Date(newEnd);
        
        // Include all days from start to end
        while (currentDate <= endDate) {
          dates.add(getLocalDateKey(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        // Must have at least 1 day selected
        if (dates.size >= 1) {
          console.log('Timeline brush filter - dates selected:', Array.from(dates));
          onDateFilter(dates);
        }
      }
    }
  }, [getValidDate, onDateFilter, getLocalDateKey]);
  
  // Reset brush selection - also clears the filter
  const handleResetSelection = useCallback(() => {
    setPanOffset(0);
    setBrushExtent(null);
    brushStartTime.current = 0;
    lastBrushDomain.current = '';
    userClickedBrush.current = false;
    // Force brush to remount by changing its key
    setBrushResetKey(prev => prev + 1);
    // Clear the date filter
    if (onDateFilter) {
      onDateFilter(null);
    }
  }, [onDateFilter]);
  
  // Handle cell click
  const handleCellClick = (entityId: string, date: Date) => {
    if (onDateFilter) {
      const dateKey = date.toISOString().split('T')[0];
      if (selectedDates?.has(dateKey)) {
        onDateFilter(null);
      } else {
        onDateFilter(new Set([dateKey]));
      }
    }
    if (onEntityFilter) {
      onEntityFilter([entityId]);
    }
    setSelectedCluster(selectedCluster === entityId ? null : entityId);
  };
  
  
  if (!dateRange || clusters.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: height,
        color: 'text.secondary',
        gap: 2,
      }}>
        <Typography variant="h6">No Timeline Data Available</Typography>
        <Typography variant="body2">
          Timeline visualization requires device nodes with connection data.
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box 
      ref={containerRef}
      sx={{ 
        width: '100%', 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isDarkMode ? '#0a0a1a' : '#fafafa',
      }}
    >
      {/* Controls Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        gap: 2, 
        px: 1.5, 
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)',
        flexWrap: 'wrap',
      }}>
        {/* Type Filters */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {Object.entries(CONNECTION_COLORS).map(([type, color]) => (
            <MuiTooltip key={type} title={type.charAt(0).toUpperCase() + type.slice(1)}>
              <Chip
                size="small"
                label={type === 'browsing' ? 'Web' : type.charAt(0).toUpperCase() + type.slice(1)}
                icon={
                  type === 'call' ? <PhoneIcon sx={{ fontSize: 14 }} /> :
                  type === 'sms' ? <SmsIcon sx={{ fontSize: 14 }} /> :
                  type === 'dwell' ? <PlaceIcon sx={{ fontSize: 14 }} /> :
                  type === 'browsing' ? <LanguageIcon sx={{ fontSize: 14 }} /> :
                  <AppsIcon sx={{ fontSize: 14 }} />
                }
                onClick={() => handleTypeToggle(type)}
                sx={{
                  bgcolor: selectedTypes.has(type) ? alpha(color, 0.3) : 'transparent',
                  borderColor: color,
                  border: '1px solid',
                  opacity: selectedTypes.has(type) ? 1 : 0.5,
                  '& .MuiChip-icon': { color },
                  '&:hover': { bgcolor: alpha(color, 0.2) },
                }}
              />
            </MuiTooltip>
          ))}
        </Stack>
        
        {/* Right: Controls */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <MuiTooltip title="Reset Selection">
            <IconButton size="small" onClick={handleResetSelection}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
          <Box sx={{ width: 1, height: 24, bgcolor: 'divider', mx: 0.5 }} />
          
          {/* Pill-style toggles */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MuiTooltip title={brushExtent 
                ? `Show only entities active within ±${overlapMinutes} min of center line (${entitiesAtCenter.size} found)` 
                : "Select a time range first, then toggle to show entities with overlapping activity"}>
                <Chip
                  size="small"
                  label={`Overlap ±${overlapMinutes}m${brushExtent && entitiesAtCenter.size > 0 ? ` (${entitiesAtCenter.size})` : ''}`}
                  onClick={() => setShowOverlapOnly(!showOverlapOnly)}
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    fontWeight: showOverlapOnly ? 700 : 400,
                    bgcolor: showOverlapOnly 
                      ? (isDarkMode ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 197, 94, 0.3)') 
                      : 'transparent',
                    border: '1px solid',
                    borderColor: showOverlapOnly 
                      ? '#22c55e' 
                      : (isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'),
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    borderRight: 'none',
                    color: showOverlapOnly 
                      ? '#fff' 
                      : (isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'),
                    '&:hover': { 
                      bgcolor: showOverlapOnly 
                        ? 'rgba(34, 197, 94, 0.5)' 
                        : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')
                    },
                  }}
                />
              </MuiTooltip>
              <Select
                size="small"
                value={overlapMinutes}
                onChange={(e) => setOverlapMinutes(Number(e.target.value))}
                renderValue={() => '▼'}
                sx={{
                  height: 24,
                  minWidth: 28,
                  '& .MuiSelect-select': {
                    py: 0,
                    px: 0.5,
                    fontSize: '0.6rem',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: showOverlapOnly 
                      ? '#22c55e' 
                      : (isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'),
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                  bgcolor: showOverlapOnly 
                    ? (isDarkMode ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 197, 94, 0.3)') 
                    : 'transparent',
                  color: showOverlapOnly 
                    ? '#fff' 
                    : (isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'),
                  '& .MuiSelect-icon': { display: 'none' },
                }}
              >
                <MenuItem value={1}>±1 min</MenuItem>
                <MenuItem value={5}>±5 min</MenuItem>
                <MenuItem value={10}>±10 min</MenuItem>
                <MenuItem value={15}>±15 min</MenuItem>
                <MenuItem value={30}>±30 min</MenuItem>
                <MenuItem value={60}>±1 hour</MenuItem>
              </Select>
            </Box>
            <MuiTooltip title="Toggle between side-by-side and stacked activity bars">
              <Chip
                size="small"
                label="Stacked"
                onClick={() => setShowDensity(!showDensity)}
                sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  fontWeight: showDensity ? 700 : 400,
                  bgcolor: showDensity 
                    ? (isDarkMode ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 197, 94, 0.3)') 
                    : 'transparent',
                  border: '1px solid',
                  borderColor: showDensity 
                    ? '#22c55e' 
                    : (isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'),
                  color: showDensity 
                    ? '#fff' 
                    : (isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'),
                  '&:hover': { 
                    bgcolor: showDensity 
                      ? 'rgba(34, 197, 94, 0.5)' 
                      : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')
                  },
                }}
              />
            </MuiTooltip>
          </Stack>
        </Stack>
      </Box>
      
      {/* Main Timeline Area */}
      <Box 
        ref={tooltipContainerRef}
        sx={{ 
          flex: 1, 
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <svg 
          width={containerWidth} 
          height={actualHeight + MARGIN.top + MARGIN.bottom}
        >
          {/* Patterns for highlighting */}
          <PatternLines
            id="highlight-pattern"
            height={6}
            width={6}
            stroke={isDarkMode ? '#00f0ff' : '#0097a7'}
            strokeWidth={1}
            orientation={['diagonal']}
          />
          
          <Group left={MARGIN.left} top={MARGIN.top}>
            {/* Background grid */}
            {timeScale && (() => {
              const ticks = timeScale.ticks(10);
              return ticks.map((tick, i) => (
                <line
                  key={`grid-${i}`}
                  x1={timeScale(tick)}
                  x2={timeScale(tick)}
                  y1={0}
                  y2={actualHeight}
                  stroke={isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
                  strokeDasharray="2,4"
                />
              ));
            })()}
            
            {/* Entity rows */}
            {filteredClusters.map((cluster, clusterIndex) => {
              const y = entityScale(cluster.id) || 0;
              const bandHeight = entityScale.bandwidth();
              const isSelected = selectedCluster === cluster.id;
              
              return (
                <Group key={cluster.id}>
                  {/* Row background - contained within the chart area */}
                  <rect
                    x={0}
                    y={y}
                    width={innerWidth}
                    height={bandHeight}
                    fill={isSelected 
                      ? alpha('#00d4ff', 0.1)
                      : clusterIndex % 2 === 0 
                        ? 'transparent' 
                        : (isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)')
                    }
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCluster(isSelected ? null : cluster.id)}
                  />
                  
                  {/* Tier indicator badge */}
                  <rect
                    x={-MARGIN.left + 6}
                    y={y + bandHeight / 2 - 8}
                    width={28}
                    height={16}
                    rx={3}
                    fill={TIER_COLORS[cluster.tier]}
                  />
                  <text
                    x={-MARGIN.left + 20}
                    y={y + bandHeight / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#fff"
                    fontSize={8}
                    fontWeight={700}
                  >
                    {TIER_LABELS[cluster.tier]}
                  </text>
                  
                  {/* Entity label - positioned after tier badge */}
                  <text
                    x={-MARGIN.left + 38}
                    y={y + bandHeight / 2}
                    textAnchor="start"
                    dominantBaseline="central"
                    fill={isSelected ? '#00d4ff' : (isDarkMode ? '#aaa' : '#444')}
                    fontSize={11}
                    fontWeight={isSelected ? 600 : 400}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCluster(isSelected ? null : cluster.id)}
                  >
                    {cluster.label.length > 11 ? cluster.label.slice(0, 11) + '…' : cluster.label}
                  </text>
                  
                  {/* Event count indicator */}
                  <text
                    x={-4}
                    y={y + bandHeight / 2}
                    textAnchor="end"
                    alignmentBaseline="middle"
                    fill={isDarkMode ? '#666' : '#999'}
                    fontSize={8}
                  >
                    {cluster.events.length > 0 ? cluster.events.length : ''}
                  </text>
                  
                  {/* Activity bars/cells */}
                  {timeScale && heatmapData
                    .filter(d => d.entityId === cluster.id)
                    .map((cell, i) => {
                      const x = timeScale(cell.date);
                      const cellWidth = Math.max(4, innerWidth / 100);
                      const intensity = intensityScale(cell.total);
                      
                      // Stack colors by type
                      const types = (Object.entries(cell.counts) as [string, number][]).sort(
                        (a, b) => b[1] - a[1]
                      );
                      const barHeight = bandHeight - 6;
                      
                      return (
                        <Group key={`${cluster.id}-${i}`}>
                          {showDensity ? (
                            // Stacked bar view
                            (() => {
                              let currentY = y + 3;
                              return types.map(([type, count], typeIndex) => {
                                const segmentHeight = (count / cell.total) * barHeight;
                                const rect = (
                                  <rect
                                    key={`${type}-${typeIndex}`}
                                    x={x}
                                    y={currentY}
                                    width={cellWidth}
                                    height={segmentHeight}
                                    fill={CONNECTION_COLORS[type] || '#888'}
                                    opacity={intensity}
                                    rx={1}
                                    style={{ cursor: 'pointer' }}
                                    onMouseEnter={(event) => {
                                      const point = localPoint(event);
                                      showTooltip({
                                        tooltipData: {
                                          entity: cluster.label,
                                          date: cell.date.toLocaleDateString(),
                                          counts: cell.counts,
                                          total: cell.total,
                                        },
                                        tooltipLeft: point?.x || 0,
                                        tooltipTop: point?.y || 0,
                                      });
                                    }}
                                    onMouseLeave={hideTooltip}
                                    onClick={() => handleCellClick(cluster.id, cell.date)}
                                  />
                                );
                                currentY += segmentHeight;
                                return rect;
                              });
                            })()
                          ) : (
                            // Side-by-side view - each activity type gets its own bar
                            (() => {
                              const typeWidth = Math.max(3, cellWidth / types.length);
                              return types.map(([type, count], typeIndex) => {
                                const typeIntensity = intensityScale(count);
                                return (
                                  <rect
                                    key={`${type}-${typeIndex}`}
                                    x={x + (typeIndex * typeWidth)}
                                    y={y + 3}
                                    width={typeWidth - 1}
                                    height={barHeight}
                                    fill={CONNECTION_COLORS[type] || '#888'}
                                    opacity={typeIntensity}
                                    rx={1}
                                    style={{ cursor: 'pointer' }}
                                    onMouseEnter={(event) => {
                                      const point = localPoint(event);
                                      showTooltip({
                                        tooltipData: {
                                          entity: cluster.label,
                                          date: cell.date.toLocaleDateString(),
                                          counts: cell.counts,
                                          total: cell.total,
                                        },
                                        tooltipLeft: point?.x || 0,
                                        tooltipTop: point?.y || 0,
                                      });
                                    }}
                                    onMouseLeave={hideTooltip}
                                    onClick={() => handleCellClick(cluster.id, cell.date)}
                                  />
                                );
                              });
                            })()
                          )}
                        </Group>
                      );
                    })
                  }
                </Group>
              );
            })}
            
            {/* Center line indicator - shows center of brush selection */}
            {brushExtent && timeScale && (() => {
              const centerTime = new Date(
                (brushExtent[0].getTime() + brushExtent[1].getTime()) / 2
              );
              const centerX = timeScale(centerTime);
              const timeStr = centerTime.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              });
              const dateStr = centerTime.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
              });
              // Only show if center is within visible range
              if (centerX >= 0 && centerX <= innerWidth) {
                return (
                  <Group>
                    {/* Center line */}
                    <line
                      x1={centerX}
                      x2={centerX}
                      y1={0}
                      y2={actualHeight}
                      stroke={isDarkMode ? '#00f0ff' : '#0097a7'}
                      strokeWidth={2}
                      strokeDasharray="8,4"
                      opacity={0.8}
                    />
                    {/* Center marker triangle at top */}
                    <polygon
                      points={`${centerX - 6},-8 ${centerX + 6},-8 ${centerX},0`}
                      fill={isDarkMode ? '#00f0ff' : '#0097a7'}
                    />
                    {/* Label with time */}
                    <text
                      x={centerX}
                      y={-20}
                      textAnchor="middle"
                      fill={isDarkMode ? '#00f0ff' : '#0097a7'}
                      fontSize={9}
                      fontWeight={600}
                    >
                      {timeStr}
                    </text>
                    <text
                      x={centerX}
                      y={-10}
                      textAnchor="middle"
                      fill={isDarkMode ? 'rgba(0, 240, 255, 0.7)' : 'rgba(0, 151, 167, 0.7)'}
                      fontSize={8}
                    >
                      {dateStr}
                    </text>
                  </Group>
                );
              }
              return null;
            })()}
            
            {/* Time axis */}
            {timeScale && (
              <AxisBottom
                top={actualHeight}
                scale={timeScale}
                tickFormat={(value) => {
                  const date = value as Date;
                  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }}
                stroke={isDarkMode ? '#444' : '#ccc'}
                tickStroke={isDarkMode ? '#444' : '#ccc'}
                tickLabelProps={{
                  fill: isDarkMode ? '#888' : '#666',
                  fontSize: 10,
                  textAnchor: 'middle',
                }}
              />
            )}
          </Group>
        </svg>
        
        {/* Tooltip */}
        {tooltipOpen && tooltipData && (
          <TooltipInPortal
            top={tooltipTop}
            left={tooltipLeft}
            style={tooltipStyles}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#00f0ff' }}>
                {tooltipData.entity}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
                {tooltipData.date}
              </Typography>
              <Stack spacing={0.5}>
                {(Object.entries(tooltipData.counts) as [string, number][]).map(([type, count]) => (
                  <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: CONNECTION_COLORS[type] || '#888' 
                      }} 
                    />
                    <Typography variant="caption" sx={{ color: '#e2e8f0' }}>
                      {type}: <strong>{count}</strong>
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                Total: <strong>{tooltipData.total}</strong> events
              </Typography>
            </Box>
          </TooltipInPortal>
        )}
      </Box>
      
      {/* Mini Timeline / Brush Selector */}
      <Box sx={{ 
        height: MINI_HEIGHT + 55, 
        borderTop: 1, 
        borderColor: 'divider',
        px: 2,
        py: 0.5,
        bgcolor: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <MuiTooltip title="Full timeline overview - always visible. Drag the blue selection area below to filter the graph and detailed timeline above by date range.">
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', mr: 0.5, cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.secondary' }}>
                {filteredClusters.length} nodes:
              </Typography>
            </MuiTooltip>
            <Chip 
              label={`${filteredClusters.filter(c => c.tier === 1).length} HUB`} 
              size="small" 
              sx={{ 
                height: 18, 
                fontSize: '0.6rem',
                bgcolor: alpha(TIER_COLORS[1], 0.3),
                fontWeight: 600,
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
            <Chip 
              label={`${filteredClusters.filter(c => c.tier === 2).length} SEC`} 
              size="small" 
              sx={{ 
                height: 18, 
                fontSize: '0.6rem',
                bgcolor: alpha(TIER_COLORS[2], 0.3),
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
            <Chip 
              label={`${filteredClusters.filter(c => c.tier === 3).length} OTH`} 
              size="small" 
              sx={{ 
                height: 18, 
                fontSize: '0.6rem',
                bgcolor: alpha(TIER_COLORS[3], 0.3),
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
            {brushExtent && entitiesAtCenter.size > 0 && (
              <MuiTooltip title={`${entitiesAtCenter.size} entities active within ±${overlapMinutes} min of center: ${centerTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}>
                <Chip 
                  label={`${entitiesAtCenter.size} @ center`} 
                  size="small" 
                  sx={{ 
                    height: 18, 
                    fontSize: '0.6rem',
                    bgcolor: isDarkMode ? 'rgba(0, 240, 255, 0.3)' : 'rgba(0, 151, 167, 0.3)',
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
              </MuiTooltip>
            )}
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', mx: 0.5 }}>
              •
            </Typography>
            <Chip 
              label={`${events.filter(e => selectedTypes.has(e.type)).length} events`} 
              size="small" 
              sx={{ 
                height: 18, 
                fontSize: '0.6rem',
                bgcolor: isDarkMode ? 'rgba(156, 39, 176, 0.3)' : 'rgba(156, 39, 176, 0.2)',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          </Stack>
          {brushExtent && formatBrushRange && (
            <Chip
              size="small"
              label={formatBrushRange}
              onDelete={handleResetSelection}
              sx={{ 
                height: 20, 
                fontSize: '0.65rem',
                bgcolor: isDarkMode ? 'rgba(0,240,255,0.2)' : 'rgba(0,151,167,0.2)',
                '& .MuiChip-deleteIcon': { fontSize: 14 }
              }}
            />
          )}
        </Box>
        
        {/* Mini Timeline Label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" sx={{ 
            fontSize: '0.65rem', 
            color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
            fontStyle: 'italic'
          }}>
            <strong>Full Timeline:</strong> Drag below to select a date range and filter the graph above
          </Typography>
        </Box>
        
        {fullTimeScale && (
            <svg 
              width={innerWidth + MARGIN.left + MARGIN.right} 
              height={MINI_HEIGHT + 20}
              onMouseDown={() => { userClickedBrush.current = true; }}
              onTouchStart={() => { userClickedBrush.current = true; }}
              style={{ cursor: 'crosshair' }}
            >
              <Group left={MARGIN.left}>
                {/* Mini timeline background */}
                <rect
                  x={0}
                  y={0}
                  width={innerWidth}
                  height={MINI_HEIGHT - 20}
                  fill={isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
                  rx={4}
                />
              
              {/* Activity density in mini timeline - colored by activity type (uses ORIGINAL data) */}
              {(() => {
                // Calculate max count from original data for proper scaling
                const miniMaxCount = Math.max(...miniHeatmapData.map(d => d.total), 1);
                return miniHeatmapData.map((cell, i) => {
                  const x = fullTimeScale(cell.date);
                  const barHeight = Math.min(MINI_HEIGHT - 25, (cell.total / miniMaxCount) * (MINI_HEIGHT - 25));
                  
                  // Get dominant activity type color
                  const types = Object.entries(cell.counts) as [string, number][];
                  const dominantType = types.length > 0 
                    ? types.reduce((a, b) => a[1] > b[1] ? a : b)[0]
                    : 'call';
                  const color = CONNECTION_COLORS[dominantType] || '#888';
                  
                  return (
                    <rect
                      key={`mini-${i}`}
                      x={x}
                      y={MINI_HEIGHT - 22 - barHeight}
                      width={4}
                      height={barHeight}
                      fill={color}
                      opacity={0.8}
                      rx={1}
                    />
                  );
                });
              })()}
              
              {/* Interactive Brush */}
              <Brush
                key={brushResetKey}
                xScale={fullTimeScale}
                yScale={scaleLinear({ domain: [0, 1], range: [MINI_HEIGHT - 20, 0] })}
                width={innerWidth}
                height={MINI_HEIGHT - 20}
                margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                handleSize={8}
                innerRef={brushRef}
                resizeTriggerAreas={['left', 'right']}
                brushDirection="horizontal"
                onChange={onBrushChange}
                onBrushEnd={onBrushEnd}
                selectedBoxStyle={{
                  fill: isDarkMode ? 'rgba(0,240,255,0.3)' : 'rgba(0,151,167,0.3)',
                  stroke: isDarkMode ? '#00f0ff' : '#0097a7',
                  strokeWidth: 2,
                }}
                useWindowMoveEvents
                renderBrushHandle={(props: BrushHandleRenderProps) => (
                  <Group left={props.x} top={0}>
                    <rect
                      x={-5}
                      y={0}
                      width={10}
                      height={MINI_HEIGHT - 20}
                      fill={isDarkMode ? '#00f0ff' : '#0097a7'}
                      rx={5}
                      style={{ cursor: 'ew-resize' }}
                    />
                    <line
                      x1={0}
                      x2={0}
                      y1={8}
                      y2={MINI_HEIGHT - 28}
                      stroke={isDarkMode ? '#0a0a1a' : '#fff'}
                      strokeWidth={2}
                    />
                  </Group>
                )}
              />
              
              {/* Mini time axis */}
              <AxisBottom
                top={MINI_HEIGHT - 18}
                scale={fullTimeScale}
                tickFormat={(value) => {
                  const date = value as Date;
                  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }}
                numTicks={Math.min(6, Math.max(2, Math.floor(innerWidth / 100)))}
                stroke={isDarkMode ? '#333' : '#ddd'}
                tickStroke={isDarkMode ? '#333' : '#ddd'}
                tickLabelProps={{
                  fill: isDarkMode ? '#666' : '#999',
                  fontSize: 9,
                  textAnchor: 'middle',
                }}
              />
            </Group>
            </svg>
        )}
      </Box>
    </Box>
  );
};

export default LinkAnalysisTimelineHeatmap;

